import React from 'react';
import { Search, ChevronDown, ChevronUp, MapPin, Building, DollarSign, ArrowUpDown, Hammer, Settings } from 'lucide-react';

export interface FilterState {
  type: 'sale' | 'rent' | 'all';
  neighborhood: string;
  city?: string;
  propertyType: string;
  priceMin: string;
  priceMax: string;
  areaMin: string;
  areaMax: string;
  rooms: string;
  searchWord: string;
  sortBy: string;
}

interface SearchFiltersProps {
  initialType: 'sale' | 'rent' | 'all';
  currentFilters?: FilterState;
  onFilterChange: (filters: FilterState) => void;
  neighborhoods: string[];
  propertyTypes: string[];
}

export default function SearchFilters({ 
  initialType, 
  currentFilters,
  onFilterChange, 
  neighborhoods, 
  propertyTypes 
}: SearchFiltersProps) {
  const [showAdvanced, setShowAdvanced] = React.useState(false);
  const [filters, setFilters] = React.useState<FilterState>(() => ({
    type: currentFilters?.type || initialType,
    neighborhood: currentFilters?.neighborhood || '',
    city: currentFilters?.city || 'Варна',
    propertyType: currentFilters?.propertyType || '',
    priceMin: currentFilters?.priceMin || '',
    priceMax: currentFilters?.priceMax || '',
    areaMin: currentFilters?.areaMin || '',
    areaMax: currentFilters?.areaMax || '',
    rooms: currentFilters?.rooms || '',
    searchWord: currentFilters?.searchWord || '',
    sortBy: currentFilters?.sortBy || 'newest'
  }));

  // Sync with currentFilters from parent or tab shifts
  React.useEffect(() => {
    if (currentFilters) {
      setFilters(prev => ({
        ...prev,
        ...currentFilters,
        type: currentFilters.type || initialType
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        type: initialType
      }));
    }
  }, [currentFilters, initialType]);

  // Propagate state changes to parent immediately on change
  const handleChange = (field: keyof FilterState, value: string) => {
    const updated = { ...filters, [field]: value };
    // Clear price range when switching between sale and rent (or all)
    // because sale prices and rent prices operate on entirely different scales
    if (field === 'type' && value !== filters.type) {
      updated.priceMin = '';
      updated.priceMax = '';
    }
    setFilters(updated);
    onFilterChange(updated);
  };

  const handleSearchSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    onFilterChange(filters);
    const target = document.getElementById('catalog-results-anchor') || 
                   document.getElementById('properties-results-heading') ||
                   document.querySelector('.max-w-7xl.mx-auto.px-4.mt-12');
    if (target) {
      target.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleReset = () => {
    // Reset to the current page context default ('sale', 'rent', or 'all')
    const targetType: 'all' | 'sale' | 'rent' = initialType || 'all';

    const resetFilters: FilterState = {
      type: targetType,
      neighborhood: '',
      city: 'Варна',
      propertyType: '',
      priceMin: '',
      priceMax: '',
      areaMin: '',
      areaMax: '',
      rooms: '',
      searchWord: '',
      sortBy: 'newest'
    };
    setFilters(resetFilters);
    onFilterChange(resetFilters);
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100 p-6 md:p-7 max-w-7xl mx-auto -mt-8 relative z-30 font-sans">
      {/* Type Toggle Tabs at the very top of search console (if on all pages or admin) */}
      <div className="flex gap-2.5 mb-5 pb-5 border-b border-gray-100 flex-wrap">
        <button
          id="search-tab-all"
          type="button"
          onClick={() => handleChange('type', 'all')}
          className={`px-4 py-2 text-xs font-bold tracking-wider rounded-xl uppercase transition-all ${
            filters.type === 'all' 
              ? 'bg-[#0F1E32] text-white shadow-sm' 
              : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
          }`}
        >
          Всички оферти
        </button>
        <button
          id="search-tab-sale"
          type="button"
          onClick={() => handleChange('type', 'sale')}
          className={`px-4 py-2 text-xs font-bold tracking-wider rounded-xl uppercase transition-all ${
            filters.type === 'sale' 
              ? 'bg-[#966D29] text-white shadow-sm' 
              : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
          }`}
        >
          ПРОДАЖБИ
        </button>
        <button
          id="search-tab-rent"
          type="button"
          onClick={() => handleChange('type', 'rent')}
          className={`px-4 py-2 text-xs font-bold tracking-wider rounded-xl uppercase transition-all ${
            filters.type === 'rent' 
              ? 'bg-[#1D608A] text-white shadow-sm' 
              : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
          }`}
        >
          ПОД НАЕМ
        </button>
      </div>

      {/* Primary search parameters grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Keyword or ID query search */}
        <div>
          <label htmlFor="filter-search-word" className="block text-xs font-bold text-gray-700 tracking-wider placeholder-slate-400 uppercase mb-1.5 flex items-center gap-1">
            <Search className="w-3.5 h-3.5 text-slate-400" /> Търсене по име / ID
          </label>
          <input
            id="filter-search-word"
            type="text"
            aria-label="Търсене по име или идентификатор"
            value={filters.searchWord}
            onChange={(e) => handleChange('searchWord', e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSearchSubmit(); }}
            placeholder="Напишете дума или ID..."
            className="w-full bg-slate-50 border border-gray-200 rounded-lg px-3.5 py-2 text-sm text-[#0F1E32] focus:bg-white focus:ring-2 focus:ring-[#C5A46E]/30 focus:border-[#C5A46E] focus:outline-none transition-all"
          />
        </div>

        {/* Neighborhood dropdown */}
        <div>
          <label htmlFor="filter-neighborhood" className="block text-xs font-bold text-gray-700 tracking-wider uppercase mb-1.5 flex items-center gap-1">
            <MapPin className="w-3.5 h-3.5 text-slate-400" /> Квартал
          </label>
          <select
            id="filter-neighborhood"
            aria-label="Изберете квартал"
            value={filters.neighborhood}
            onChange={(e) => handleChange('neighborhood', e.target.value)}
            className="w-full bg-slate-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-[#0F1E32] focus:bg-white focus:ring-2 focus:ring-[#C5A46E]/30 focus:border-[#C5A46E] focus:outline-none transition-all appearance-none cursor-pointer"
          >
            <option value="">Всички квартали</option>
            {neighborhoods.map((n, idx) => {
              const val = typeof n === 'object' ? (n as any)?.name || (n as any)?.label || '' : String(n || '');
              if (!val) return null;
              return <option key={idx} value={val}>{val}</option>;
            })}
          </select>
        </div>

        {/* Property Type dropdown */}
        <div>
          <label htmlFor="filter-property-type" className="block text-xs font-bold text-gray-700 tracking-wider uppercase mb-1.5 flex items-center gap-1">
            <Building className="w-3.5 h-3.5 text-slate-400" /> Вид имот
          </label>
          <select
            id="filter-property-type"
            aria-label="Изберете вид имот"
            value={filters.propertyType}
            onChange={(e) => handleChange('propertyType', e.target.value)}
            className="w-full bg-slate-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-[#0F1E32] focus:bg-white focus:ring-2 focus:ring-[#C5A46E]/30 focus:border-[#C5A46E] focus:outline-none transition-all appearance-none cursor-pointer"
          >
            <option value="">Всички видове</option>
            {propertyTypes.map((type, idx) => {
              const val = typeof type === 'object' ? (type as any)?.name || (type as any)?.label || '' : String(type || '');
              if (!val) return null;
              return <option key={idx} value={val}>{val}</option>;
            })}
          </select>
        </div>

        {/* Sort SorterDropdown */}
        <div>
          <label htmlFor="filter-sort-by" className="block text-xs font-bold text-gray-700 tracking-wider uppercase mb-1.5 flex items-center gap-1">
            <ArrowUpDown className="w-3.5 h-3.5 text-slate-400" /> Сортирай по
          </label>
          <select
            id="filter-sort-by"
            aria-label="Сортиране на офертите"
            value={filters.sortBy}
            onChange={(e) => handleChange('sortBy', e.target.value)}
            className="w-full bg-slate-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-[#0F1E32] focus:bg-white focus:ring-2 focus:ring-[#C5A46E]/30 focus:border-[#C5A46E] focus:outline-none transition-all appearance-none cursor-pointer"
          >
            <option value="newest">Най-нови оферти</option>
            <option value="price-asc">Цена: възходяща ↑</option>
            <option value="price-desc">Цена: низходяща ↓</option>
            <option value="area-desc">Площ: най-големи ↓</option>
          </select>
        </div>
      </div>

      {/* Advanced Filters Drawer (Price range, area range) */}
      {showAdvanced && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-6 pt-5 border-t border-gray-100 dynamic-advanced-filters">
          {/* Price Range */}
          <div>
            <span className="block text-xs font-bold text-gray-700 tracking-wider uppercase mb-1.5 flex items-center gap-1">
              <DollarSign className="w-3.5 h-3.5 text-slate-400" /> Бюджет в EUR (€)
            </span>
            <div className="flex gap-2">
              <input
                id="filter-price-min"
                type="number"
                aria-label="Минимална цена в евро"
                value={filters.priceMin}
                onChange={(e) => handleChange('priceMin', e.target.value)}
                placeholder="Минимум"
                className="w-1/2 bg-slate-50 border border-gray-200 rounded-lg px-3.5 py-2 text-sm text-[#0F1E32] focus:bg-white focus:outline-none"
              />
              <input
                id="filter-price-max"
                type="number"
                aria-label="Максимална цена в евро"
                value={filters.priceMax}
                onChange={(e) => handleChange('priceMax', e.target.value)}
                placeholder="Максимум"
                className="w-1/2 bg-slate-50 border border-gray-200 rounded-lg px-3.5 py-2 text-sm text-[#0F1E32] focus:bg-white focus:outline-none"
              />
            </div>
          </div>

          {/* Area Range */}
          <div>
            <span className="block text-xs font-bold text-gray-700 tracking-wider uppercase mb-1.5 flex items-center gap-1">
              📐 Площ в кв.м. (m²)
            </span>
            <div className="flex gap-2">
              <input
                id="filter-area-min"
                type="number"
                aria-label="Минимална площ в квадратни метри"
                value={filters.areaMin}
                onChange={(e) => handleChange('areaMin', e.target.value)}
                placeholder="Мин"
                className="w-1/2 bg-slate-50 border border-gray-200 rounded-lg px-3.5 py-2 text-sm text-[#0F1E32] focus:bg-white focus:outline-none"
              />
              <input
                id="filter-area-max"
                type="number"
                aria-label="Максимална площ в квадратни метри"
                value={filters.areaMax}
                onChange={(e) => handleChange('areaMax', e.target.value)}
                placeholder="Макс"
                className="w-1/2 bg-slate-50 border border-gray-200 rounded-lg px-3.5 py-2 text-sm text-[#0F1E32] focus:bg-white focus:outline-none"
              />
            </div>
          </div>
        </div>
      )}

      {/* Control Buttons */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6 pt-4 border-t border-gray-100">
        <button
          id="btn-toggle-advanced"
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="text-xs text-[#0F1E32] hover:text-[#C5A46E] font-bold flex items-center gap-1.5 transition-colors cursor-pointer outline-none select-none"
        >
          {showAdvanced ? (
            <>
              <ChevronUp className="w-4 h-4 text-[#C5A46E]" />
              Скрий разширените филтри
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4 text-[#C5A46E]" />
              Още разширени филтри
            </>
          )}
        </button>

        <div className="flex gap-2.5 w-full sm:w-auto">
          <button
            id="btn-search-clear"
            type="button"
            onClick={handleReset}
            className="w-1/2 sm:w-auto text-xs text-red-600 hover:text-red-700 font-bold border border-red-200 hover:bg-red-50/60 rounded-lg px-4.5 py-2 cursor-pointer transition"
          >
            Изчисти
          </button>
          <button
            id="btn-search-trigger"
            type="button"
            onClick={handleSearchSubmit}
            className="w-1/2 sm:w-auto bg-[#007BB5] hover:bg-[#00699B] text-white text-xs font-bold tracking-wider uppercase rounded-lg px-6 py-2.5 shadow-sm hover:shadow active:scale-95 cursor-pointer transition flex items-center justify-center gap-1.5"
          >
            <Search className="w-3.5 h-3.5 stroke-[2.5]" />
            <span>Търси</span>
          </button>
        </div>
      </div>
    </div>
  );
}
